import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Helmet from 'react-helmet';
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  TablePagination,
  Button,
  TableSortLabel,
} from '@material-ui/core';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { debounce } from 'lodash';
import { useHistory } from 'react-router-dom';

import OrganizationTotalsBar from 'retired/OrganizationTotalsBar/index';
import { TableBox } from 'retired/TableBox';
import TablePaginationActions from 'retired/TablePaginationActions';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { selectUser } from 'entities/user/selectors';
import Organization from 'entities/organization/model';
import { selectDisplayedOrganizations } from 'entities/organization/selectors';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import OrganizationsPageBanner from 'assets/pageBanners/organizations.avif';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';

import OrganizationListTableRow from './OrganizationListTableRow';
import UnblockNonprofitModal from './UnblockNonprofitModal';
import reducer from './reducer';
import epics from './epics';
import {
  selectLoading,
  selectFetchParams,
  selectError,
  selectInitialized,
  selectTotalOrganizations,
  selectTotalDonations,
  selectTotalVolunteerHours,
  selectTotalDeeds,
  selectTotalDonationsLoading,
  selectTotalVolunteerHoursLoading,
  selectTotalDeedsLoading,
} from './selectors';
import {
  setFetchParamsAction,
  init,
  fetchDonationsTotal,
  fetchDeedsTotal,
  fetchVolunteerTimesTotal,
} from './types';

interface HeaderColumn {
  id: string;
  label: string;
  sorted?: boolean;
  organizations: Organization[];
}

const OrganizationsListPage = (): ReactElement => {
  const dispatch = useDispatch();
  useInjectEpics({ key: 'organizationsListPage', epics });
  useInjectReducer({ key: 'organizationsListPage', reducer });
  const searchInputRef = useRef<{ value?: string }>();
  const history = useHistory();

  const loading = useSelector(selectLoading);
  const fetchParams = useSelector(selectFetchParams);
  const organizations = useSelector(selectDisplayedOrganizations);
  const initialized = useSelector(selectInitialized);
  const totalOrganizations = useSelector(selectTotalOrganizations);

  const user = useSelector(selectUser);
  const error = useSelector(selectError);

  const donationTotal = useSelector(selectTotalDonations);
  const volunteerHoursTotal = useSelector(selectTotalVolunteerHours);
  const deedsTotal = useSelector(selectTotalDeeds);

  const donationTotalLoadingState = useSelector(selectTotalDonationsLoading);
  const volunteerHoursTotalLoadingState = useSelector(selectTotalVolunteerHoursLoading);
  const deedsTotalLoadingState = useSelector(selectTotalDeedsLoading);

  const employeeAffiliationsView =
    user?.hasFeature(Features.EMPLOYEE_AFFILIATIONS) &&
    user?.hasPrivilege(Privileges.EMPLOYEE_AFFILIATIONS_VIEW);

  const [sortLabel, setSortLabel] = useState<{
    sortedBy: keyof Organization;
    order: 'asc' | 'desc';
  }>({
    sortedBy: 'name',
    order: 'asc',
  });

  const [unblockNonprofitModalOpen, setUnblockNonprofitModalOpen] = useState(false);

  useEffect(() => {
    if (user) {
      if (!user.hasPrivilege(Privileges.ORGANIZATIONS_VIEW) || !user.organization.isCompany()) {
        history.replace('/');
      }

      dispatch(init());
      dispatch(fetchDeedsTotal());

      if (user.hasPrivilege(Privileges.DONATION_VIEW) && user.hasFeature(Features.DONATIONS)) {
        dispatch(fetchDonationsTotal());
      }

      if (user.hasPrivilege(Privileges.VOLUNTEERTIME) && user.hasFeature(Features.VOLUNTEERING)) {
        dispatch(fetchVolunteerTimesTotal());
      }
    }
  }, [user]);

  useEffect(() => {
    dispatch(setFetchParamsAction({ sort: sortLabel.sortedBy, order: sortLabel.order }));
  }, [sortLabel]);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(setFetchParamsAction({ limit: Number(event.target.value) }));
  };

  const handleSearch = (search: string): void => {
    dispatch(setFetchParamsAction({ search }));
  };

  const handleSearchDebounced = debounce((search) => handleSearch(search), 600);

  const handleCloseUnblockNonprofitModal = () => setUnblockNonprofitModalOpen(false);

  const columns = [
    { id: 'name', label: 'Name', sorted: true },
    { id: 'location', label: 'Located', sorted: false },
    { id: 'region', label: 'Region', sorted: false },
    employeeAffiliationsView && {
      id: 'affiliationsCount',
      label: 'Affiliations',
      sorted: true,
    },
  ];

  const goToPage = (__: unknown, pageNum: number): void => {
    dispatch(setFetchParamsAction({ pageNum }));
  };

  const onSortedLabelClick = (item: HeaderColumn): void => {
    if (item.id === sortLabel.sortedBy) {
      const order = sortLabel.order === 'desc' ? 'asc' : 'desc';
      setSortLabel({
        sortedBy: item.id,
        order,
      });
    } else {
      setSortLabel({
        sortedBy: item.id,
        order: 'desc',
      });
    }
  };

  const handleSetUnblockNonprofitModalOpenClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUnblockNonprofitModalOpen(true);
    event.stopPropagation();
  };

  return (
    <>
      <UnblockNonprofitModal
        open={unblockNonprofitModalOpen}
        onClickClose={handleCloseUnblockNonprofitModal}
      />
      <Helmet title="Organizations" />
      <PageBannerContainer color="#D9FFCE" backgroundImage={OrganizationsPageBanner}>
        <h1>Organizations</h1>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1em',
            justifyContent: 'flex-end',
          }}
        >
          {(user?.isAdmin || user?.hasPrivilege(Privileges.NONPROFITS_UNBLOCK)) && (
            <Button
              variant="outlined"
              size="large"
              onClick={handleSetUnblockNonprofitModalOpenClick}
              endIcon={<LockOpenIcon />}
            >
              Unblock nonprofit
            </Button>
          )}
          {user?.hasPrivilege(Privileges.PARTNER_VIEW) && (
            <Button
              variant="outlined"
              size="large"
              onClick={(): void => history.push('/organizations/partners')}
            >
              Nonprofit Affiliations
            </Button>
          )}
          {(user?.hasFeature(Features.USER_NOMINATE_NONPROFIT) ||
            user?.hasPrivilege(Privileges.DEED_INTERNAL_HANDLE_NONPROFIT_NOMINATIONS)) && (
            <Button
              variant="outlined"
              size="large"
              onClick={(): void => history.push('/nonprofit-nominations')}
            >
              Nonprofit Nominations
            </Button>
          )}
        </div>
      </PageBannerContainer>
      {user?.organization?.isCompany() && (
        <OrganizationTotalsBar
          totalDeeds={deedsTotal}
          totalHours={volunteerHoursTotal}
          totalDonations={donationTotal}
          user={user}
          loading={
            donationTotalLoadingState || volunteerHoursTotalLoadingState || deedsTotalLoadingState
          }
        />
      )}
      {!initialized && loading && <LinearProgress />}
      {!loading && error && <Typography>There was an error loading your organizations</Typography>}
      {initialized && (
        <TableBox
          fixedTop={
            <Toolbar style={{ marginTop: -4, justifyContent: 'flex-end' }}>
              <TextField
                type="search"
                size="small"
                placeholder="Search"
                inputRef={searchInputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear"
                        onClick={(): void => {
                          if (searchInputRef.current) {
                            searchInputRef.current.value = '';
                          }
                          handleSearch('');
                        }}
                        size="small"
                        disabled={!fetchParams.get('search')}
                      >
                        <ClearIcon fontSize="inherit" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                  handleSearchDebounced(event.target.value)
                }
                style={{
                  width: 350,
                  marginLeft: 10,
                }}
              />
            </Toolbar>
          }
          fixedBottom={
            <>
              <div>
                <strong>Total: </strong>
                {`${totalOrganizations} Organizations`}
              </div>
              <TablePagination
                component="div"
                rowsPerPageOptions={[]} // hides dropdown
                count={totalOrganizations}
                rowsPerPage={fetchParams.get('limit')}
                page={fetchParams.get('pageNum')}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
                onChangePage={goToPage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </>
          }
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.filter(Boolean).map((item) => (
                  <TableCell key={item.id} size="medium">
                    {item.sorted ? (
                      <TableSortLabel
                        direction={sortLabel.order}
                        active={sortLabel.sortedBy === item.id}
                        onClick={(): void => {
                          onSortedLabelClick(item);
                        }}
                      >
                        {item.label}
                      </TableSortLabel>
                    ) : (
                      item.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody
              style={loading ? { opacity: 0.3, cursor: 'wait', pointerEvents: 'none' } : null}
            >
              {organizations.size > 0 ? (
                organizations.map(
                  (organization) =>
                    organization && (
                      <OrganizationListTableRow
                        organization={organization}
                        employeeAffiliationsView={employeeAffiliationsView}
                        key={organization.id}
                      />
                    )
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography
                      style={{
                        padding: '16px 0',
                      }}
                    >
                      There are no search results
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableBox>
      )}
    </>
  );
};

export default OrganizationsListPage;
